/**
 * components/Card/CardFooter
 * The `12px 15px` strip at the bottom of a card, over #FAFAFA with a top rule.
 * Used for the sentence that explains a card's rule ("Spends under the
 * auto-approve floor are never held, whatever the rules say.") and for the
 * consequence line plus actions on a reconciliation card.
 */
import type { ReactNode } from 'react';
import { StyleSheet, Text, View } from 'react-native';
import { neutral } from '../../theme/colors';
import { card as cardSpace } from '../../theme/spacing';
import { sans, size as fontSize, leading } from '../../theme/typography';

export interface CardFooterProps {
  /** The explanatory sentence. Omit when the footer is only actions. */
  note?: string;
  /** Buttons or chips, right-aligned. */
  right?: ReactNode;
  children?: ReactNode;
}

export function CardFooter({ note, right, children }: CardFooterProps) {
  return (
    <View style={styles.footer}>
      {children ?? (
        <>
          {!!note && <Text style={styles.note}>{note}</Text>}
          {!!right && <View style={styles.right}>{right}</View>}
        </>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  footer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    paddingVertical: cardSpace.headerPaddingY,
    paddingHorizontal: cardSpace.headerPaddingX,
    backgroundColor: neutral.surface2,
    borderTopWidth: 1,
    borderTopColor: neutral.border,
  },
  note: {
    ...sans('400'),
    fontSize: fontSize.meta,
    lineHeight: leading(fontSize.meta),
    color: neutral.grey600,
    flex: 1,
    minWidth: 0,
  },
  right: { flexDirection: 'row', alignItems: 'center', gap: 8, flexShrink: 0 },
});

export default CardFooter;
